import { isValidElement, useEffect, useRef } from "react";

import i18n from "i18next";
import moment from "moment";
import path from "path-browserify";
import slugify from "slugify";

import {
  downloadResponseAsFile,
  handleError,
  requestWrapper,
} from "~/app/utils/requests";

import { BASE_URL } from "~/app/constant/env";

import { normalizeStringWithoutSC } from "./search";

const { t } = i18n;

/**
 * Lougout function:
 * - triggers a12n methods to remove access & refresh cookies
 * - then refresh the page and send the user to the signin form
 */
export async function logout() {
  requestWrapper.post("/a12n/refresh/logout").finally(() => {
    requestWrapper.post("/a12n/access/logout").finally(() => {
      /**
       * Send the user to the sign in page without using the javascript router
       * This will lead to a page refresh which is what we want :
       * a page refresh will clear all persisting data in react query
       */
      window.location = path.join(BASE_URL, "/login");
    });
  });
}

// Function to check if a string is a JSON
export const isJSON = (string) => {
  try {
    JSON.parse(string);
  } catch {
    return false;
  }
  return true;
};

// Function to check if a string is a valid email address
const emailRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
export const isEmail = (string) => {
  return emailRegex.test(string);
};

// Function to check if something is an object
export const isObject = function isObject(obj) {
  return obj === Object(obj);
};

// Function to return a user name
export const getUserName = (user, fallback = t("Unknown user")) =>
  user ? `${user.first_name} ${user.last_name}` : fallback;

// Function to get a number with its ordinal (ie: 1 => 1st / 22 => 22nd / ...)
export const getNumberWithOrdinal = (number) => {
  var s = ["th", "st", "nd", "rd"],
    v = number % 100;
  return number + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Function to get a boolean from a string
export const getBooleanFromString = (value) => {
  if (typeof value === "string") {
    switch (value.toLowerCase()) {
      case "true":
        return true;
      case "false":
        return false;
      default:
        return value;
    }
  } else {
    return value;
  }
};

// Function to merge ref from parent and current one in a single reference value
export function useCombinedRefs(...refs) {
  const targetRef = useRef();
  useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(targetRef.current);
      } else {
        ref.current = targetRef.current;
      }
    });
  }, [refs]);
  return targetRef;
}

/**
 * Function to escape a regex from a string
 * @param {string} string
 * @returns An escaped string to be used inside a regex. Example: "http://regreview.heka.ai" will return "http:\\/\\/regreview\\.heka\\.ai"
 */
export const escapeRegex = (string) =>
  string.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

/**
 * Function that format legal entities
 * @param {array} legalEntities
 * @returns properly formatted legal entities
 */
export const formatLegalEntities = (legalEntities) =>
  legalEntities.map((item) => ({
    id: item.legal_entity.id,
    label: item.legal_entity.label,
  }));

/**
 * Function that format reglow members in tab
 * @param {array} members
 * @returns properly formatted members
 */
export const formatRegflowMembers = (members) =>
  members?.map((member) => ({
    name: getUserName(member.user),
    role: member.role.label,
  }));

/**
 * Function that attribute color to risk intensities
 * @param {number} levelId
 * @returns css corresponding className
 */
export const levelColorRiskIntensities = (levelId) => {
  switch (levelId) {
    case 1:
      return "na";
    case 2:
      return "low";
    case 3:
      return "medium";
    case 4:
      return "high";
    default:
      return "";
  }
};

/**
 * Function that link tooltip position to parent element
 * @returns trigger closest html element with data-scrollable-container attribute
 */
export const getPopupContainer = (trigger) =>
  trigger.closest("[data-scrollable-container]") || document.body;

/**
 * Function that retrieve users assigned to a regtask
 * @returns array of users id
 */
export const getAssignmentsUserIds = (regtask) => {
  return regtask?.assignments?.map((assignment) => assignment.user.id);
};

/**
 * Function that format date moment object in local user timezone
 * @returns formatted date moment in timezone
 */
export const getLocalMoment = (date) => moment.utc(date).local();

/**
 * Function that sort alphabetically strings in array
 * @returns sorted array
 */
export const sortAlphabetically = (array = []) => {
  const newArray = [...array];
  return newArray?.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
};

/**
 * Function that gets raw text from a react element (assuming it has strings contained in childrens)
 * @param {reactElement} reactElement
 * @returns string
 */
export const extractStringFromReactElement = (reactElement) => {
  if (typeof reactElement === "string")
    return reactElement.replace(/\s\s+/g, " ").trim();
  else if (isValidElement(reactElement)) {
    return extractStringFromReactElement(reactElement.props.children);
  } else if (Array.isArray(reactElement)) {
    return reactElement.map((e) => extractStringFromReactElement(e)).join(" ");
  } else return reactElement.toString();
};

/**
 * Function to use in ant design select as filterOption prop
 * @param {reactElement} reactElement
 * @returns string
 */
export const getAntdFilterOption =
  (searchTextProps = "label") =>
  (inputValue, option) => {
    const isGroup = Array.isArray(option.options);
    if (isGroup) {
      return false;
    }
    const optionText = extractStringFromReactElement(option[searchTextProps]);
    return normalizeStringWithoutSC(optionText).includes(
      normalizeStringWithoutSC(inputValue)
    );
  };

/**
 * Download function:
 * - triggers c13s download methods to download article or uploaded files
 */
export const downloadFiles = (paths) =>
  requestWrapper
    .post("/c13s/download", { paths }, { responseType: "blob" })
    .then((res) => {
      downloadResponseAsFile(res);
    })
    .catch((err) => {
      handleError(
        err,
        "An error has occurred while downloading the file. Please try again"
      );
    });

/**
 * Function to clear all errors from an ant design form
 * @param {object} antFormInstance ant design form instance
 */
export const clearAntFormErrors = (antFormInstance) => {
  const fieldNames = antFormInstance.getFieldsError();
  antFormInstance.setFields(
    fieldNames.map((name) => ({ name: name.name[0], errors: [] }))
  );
};

/**
 * Function to format infiniteQuery response
 * @param {object} infiniteQueryData - The object return by an infiniteQuery
 * @returns {array} An array of items contained in all the pages
 */
export const flattenInfiniteQueryItems = (infiniteQueryData) =>
  infiniteQueryData?.pages?.reduce((acc, curr) => [...acc, ...curr.data], []);

/**
 * Function to slugify string for search purpose
 * @param {string} - The string we want to normalize
 * @returns {string} - The string without accent and capital letters
 */
export const slugifyString = (str) =>
  slugify(str, {
    lower: true,
    remove: /[^\w\s]/g,
    replacement: "",
  });

/**
 * Function to get info (hostname, client, product, environment) from window location
 */
export const getHostnameInfo = () => {
  const hostname = window.location.hostname;
  const regex = /((.*?)\.)?(regreview|strategicwatch)(\.(.*?))?(\..*\..*)/g;
  const match = regex.exec(hostname) || [];
  return {
    hostname,
    client: match[2],
    product: match[3] || "regreview",
    environment: hostname.includes("localhost") ? "local" : match[5],
  };
};
